import {
  faArrowLeft,
  faCircleCheck,
  faClockRotateLeft,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';

import useClientDocumentTemplates from '../../api/super-admin/clients/useClientDocumentTemplates';
import useCreateClientAdminUser from '../../api/super-admin/clients/useCreateClientAdminUser';
import useCreateClientVroomConfiguration from '../../api/super-admin/clients/useCreateClientVroomConfiguration';
import useGetClient from '../../api/super-admin/clients/useGetClient';
import useSaveClient from '../../api/super-admin/clients/useSaveClient';
import useUploadClientLogo from '../../api/super-admin/clients/useUploadClientLogo';
import useGetRequest from '../../api/useGetRequest';
import MainButton from '../../components/agv-button';
import GenericModal from '../../components/agv-generic-modal';
import GenericPageContainer from '../../components/agv-generic-page-container';
import Stepper from '../../components/agv-stepper';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import { EMPTY_CLIENT_INFORMATION } from './client-defaults';
import { computeJsonDiff } from './client-diff';
import { Banner, Card } from './clients-ui';
import ClientChangesReviewModal from './components/client-changes-review-modal';
import ClientHistoryModal from './components/client-history-modal';
import AdminUserStep from './steps/admin-user-step';
import DocumentsStep from './steps/documents-step';
import InformationStep, {
  isInformationStepValid,
} from './steps/information-step';
import OptionsStep from './steps/options-step';
import PricingStep, {
  isPricingValid,
  PRICING_KEYS,
} from './steps/pricing-step';

const STEP = {
  INFORMATION: 0,
  OPTIONS: 1,
  PRICING: 2,
  DOCUMENTS: 3,
  ADMIN: 4,
  SUMMARY: 5,
};

const STEPS = [
  { label: 'Informations' },
  { label: 'Options' },
  { label: 'Tarification' },
  { label: 'Documents (WIP)' },
  { label: 'Administrateur' },
  { label: 'Récapitulatif' },
];

const toOptionalNumber = (value) =>
  value === '' || value == null ? undefined : Number(value);

const countEnabled = (value) => {
  if (value === true) return 1;
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    return 0;
  return Object.values(value).reduce(
    (sum, child) => sum + countEnabled(child),
    0
  );
};

const toInformation = (client) => ({
  name: client.name || '',
  siret: client.siret || '',
  tva: client.tva || '',
  street: client.street || '',
  zipcode: client.zipcode || '',
  city: client.city || '',
  country: client.country || '',
  is_email_sending_enabled: client.is_email_sending_enabled === 1,
  is_sms_sending_enabled: client.is_sms_sending_enabled === 1,
});

/**
 * Données envoyées à l'API (sans l'usine de départ, propre à la création).
 * Sert aussi de base de comparaison avant / après pour un client existant.
 */
const toClientPayload = ({ information, featuresV2, pricing }) => ({
  ...information,
  features_v2: featuresV2,
  pricing: Object.fromEntries(
    PRICING_KEYS.filter(
      (key) => pricing?.[key] != null && pricing[key] !== ''
    ).map((key) => [key, Number(pricing[key])])
  ),
});

const SummaryRow = ({
  isDone,
  isWarning,
  label,
  detail,
  actionText = 'Modifier',
  onAction,
  isActionLoading,
}) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-3 tw-py-2.5 last:tw-border-b-0">
    <FontAwesomeIcon
      icon={isWarning ? faTriangleExclamation : faCircleCheck}
      className={`tw-size-4 ${
        isWarning
          ? 'tw-text-amber-500'
          : isDone
            ? 'tw-text-status-positive'
            : 'tw-text-[#9aa0a6]'
      }`}
    />
    <span className="tw-w-40 tw-text-[13px] tw-font-medium tw-text-black">
      {label}
    </span>
    <span className="tw-flex-1 tw-text-[12px] tw-text-[#76808c]">{detail}</span>
    {onAction && (
      <MainButton
        variant="link"
        text={actionText}
        onClick={onAction}
        isLoading={isActionLoading}
      />
    )}
  </div>
);

export default function ClientWizard({ clientId, onClientCreated, onExit }) {
  // Seul l'identifiant reçu au montage compte : à la création, l'URL passe à
  // l'id du nouveau client sans qu'il faille recharger la fiche (on écraserait
  // le logo en cours d'envoi).
  const [initialClientId] = useState(clientId);
  const isEdition = initialClientId != null;

  const [step, setStep] = useState(STEP.INFORMATION);
  const [savedClientId, setSavedClientId] = useState(initialClientId);
  // Dernière version enregistrée du client, telle que renvoyée par l'API :
  // référence de l'avant / après et des modifications non enregistrées.
  const [savedClient, setSavedClient] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const [information, setInformation] = useState(EMPTY_CLIENT_INFORMATION);
  const [factory, setFactory] = useState(null);
  const [featuresV2, setFeaturesV2] = useState({});
  const [pricing, setPricing] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [stepAfterSave, setStepAfterSave] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const { data: clientsData } = useGetRequest('/super-admin/clients', 0);
  const { data: defaults } = useGetRequest('/super-admin/clients/defaults', 0);
  const { isLoading: isFetching, fetchClient } = useGetClient();
  const { isLoading: isSaving, saveClient } = useSaveClient();
  const { isLoading: isLogoUploading, uploadLogo } = useUploadClientLogo();
  const { isUploading, archivingId, uploadTemplate, archiveTemplate } =
    useClientDocumentTemplates();
  const { isLoading: isCreatingAdmin, createAdminUser } =
    useCreateClientAdminUser();
  const { isLoading: isCreatingVroom, createVroomConfiguration } =
    useCreateClientVroomConfiguration();

  const isClientSaved = savedClientId != null;

  const hydrate = (client) => {
    setSavedClient(client);
    setInformation(toInformation(client));
    setFeaturesV2(client.features_v2 || {});
    setPricing(client.pricing || {});
    setTemplates(client.document_templates || []);
    setAdminUsers(client.admin_users || []);
  };

  // Client existant : sa fiche. Nouveau client : les valeurs par défaut de
  // l'API (options et tarification).
  useEffect(() => {
    if (!isEdition) return;

    const loadClient = async () => {
      const client = await fetchClient(initialClientId);

      if (!client) {
        onExit();
        return;
      }

      hydrate(client);
      setIsHydrated(true);
    };

    loadClient();
  }, [initialClientId]);

  useEffect(() => {
    if (isEdition || isHydrated || !defaults) return;

    setFeaturesV2(defaults.features_v2);
    setPricing(defaults.pricing);
    setIsHydrated(true);
  }, [defaults]);

  const clientsOptions = useMemo(
    () =>
      (clientsData?.clients || [])
        .filter((client) => client.client_id !== savedClientId)
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        .map((client) => ({
          value: String(client.client_id),
          name: `${client.client_id} - ${client.name}`,
        })),
    [clientsData, savedClientId]
  );

  const payload = toClientPayload({ information, featuresV2, pricing });

  const unsavedChanges = useMemo(
    () =>
      savedClient
        ? computeJsonDiff(
            toClientPayload({
              information: toInformation(savedClient),
              featuresV2: savedClient.features_v2 || {},
              pricing: savedClient.pricing || {},
            }),
            payload
          )
        : [],
    [savedClient, information, featuresV2, pricing]
  );
  const hasUnsavedChanges = isClientSaved && unsavedChanges.length > 0;

  const isInformationValid = isInformationStepValid(information, factory);
  const isPricingStepValid = isPricingValid(pricing);

  const handleCopyFromClient = async (sourceClientId) => {
    const sourceClient = await fetchClient(sourceClientId);
    if (!sourceClient) return;

    setFeaturesV2(sourceClient.features_v2 || {});
    setPricing({
      ...(defaults?.pricing || {}),
      ...(sourceClient.pricing || {}),
    });
  };

  const handleLogoFile = async (file) => {
    // Avant la création, le logo attend que le client ait un identifiant.
    if (!isClientSaved) {
      setLogoFile(file);
      return;
    }

    setLogoFile(file);
    const url = await uploadLogo(savedClientId, file);
    if (url) setSavedClient((previous) => ({ ...previous, logo_url: url }));
    setLogoFile(null);
  };

  const createClient = async () => {
    const client = await saveClient(null, {
      ...payload,
      ...(factory && {
        factory: {
          ...factory,
          latitude: toOptionalNumber(factory.latitude),
          longitude: toOptionalNumber(factory.longitude),
        },
      }),
    });
    if (!client) return false;

    hydrate(client);
    setSavedClientId(client.client_id);
    onClientCreated(client.client_id);

    if (logoFile) {
      const url = await uploadLogo(client.client_id, logoFile);
      if (url) setSavedClient((previous) => ({ ...previous, logo_url: url }));
      setLogoFile(null);
    }

    return true;
  };

  /**
   * Enregistre puis va à `nextStep` (ou reste sur l'étape). Pour un client
   * existant, les modifications passent d'abord par la relecture avant /
   * après ; sans modification, rien n'est envoyé.
   */
  const save = async (nextStep = null) => {
    if (!isClientSaved) {
      const isCreated = await createClient();
      if (isCreated && nextStep != null) setStep(nextStep);
      return;
    }

    if (!hasUnsavedChanges) {
      if (nextStep != null) setStep(nextStep);
      return;
    }

    setStepAfterSave(nextStep);
    setIsReviewOpen(true);
  };

  const confirmSave = async () => {
    const client = await saveClient(savedClientId, payload);
    if (!client) return;

    hydrate(client);
    setIsReviewOpen(false);
    if (stepAfterSave != null) setStep(stepAfterSave);
  };

  const exit = () => {
    if (hasUnsavedChanges) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    onExit();
  };

  const goToNextStep = () => {
    if (step === STEP.PRICING) {
      save(STEP.PRICING + 1);
      return;
    }

    setStep((previous) => Math.min(previous + 1, STEP.SUMMARY));
  };

  const canGoToStep = (targetStep) => {
    if (targetStep <= STEP.PRICING) return true;
    return isClientSaved;
  };

  const isNextDisabled =
    (step === STEP.INFORMATION && !isInformationValid) ||
    (step === STEP.PRICING && (!isPricingStepValid || !isInformationValid));

  const nextLabel =
    step === STEP.PRICING
      ? isClientSaved
        ? 'Enregistrer et continuer'
        : 'Créer le client'
      : step === STEP.DOCUMENTS && templates.length === 0
        ? 'Passer cette étape'
        : 'Suivant';

  const title = isClientSaved
    ? `${savedClient?.name || information.name || 'Client'} · ID ${savedClientId}`
    : 'Nouveau client';

  const vroomConfiguration = savedClient?.vroom_configuration;

  return (
    <GenericPageContainer
      title={title}
      subtitle={
        isClientSaved
          ? 'Paramétrage du client : options, documents et administrateurs'
          : 'Création d’un client : données, options, tarification, documents et premier administrateur'
      }
      filters={
        <>
          {isClientSaved && (
            <MainButton
              variant="secondary"
              icon={faClockRotateLeft}
              text="Historique"
              onClick={() => setIsHistoryOpen(true)}
            />
          )}
          <MainButton
            variant="secondary"
            icon={faArrowLeft}
            text="Liste des clients"
            onClick={exit}
          />
        </>
      }
    >
      <div className="tw-mx-auto tw-flex tw-max-w-[880px] tw-flex-col tw-pb-10 tw-pt-4">
        <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-[#e6e8ec] tw-bg-white tw-px-6 tw-pt-6">
          <Stepper
            steps={STEPS}
            currentStep={step}
            onStepClick={setStep}
            isStepDisabled={(index) => !canGoToStep(index)}
          />
        </div>

        <div className="tw-mt-4">
          {!isHydrated ? (
            <Card
              title={
                isEdition ? 'Chargement du client…' : 'Chargement des valeurs…'
              }
            />
          ) : (
            <>
              {step === STEP.INFORMATION && (
                <InformationStep
                  information={information}
                  setInformation={setInformation}
                  factory={factory}
                  setFactory={setFactory}
                  existingFactories={
                    isClientSaved ? savedClient?.factories || [] : null
                  }
                  logoUrl={savedClient?.logo_url}
                  logoFile={logoFile}
                  setLogoFile={handleLogoFile}
                  isLogoUploading={isLogoUploading}
                />
              )}
              {step === STEP.OPTIONS && (
                <OptionsStep
                  featuresV2={featuresV2}
                  setFeaturesV2={setFeaturesV2}
                  defaultFeaturesV2={defaults?.features_v2}
                  clientsOptions={clientsOptions}
                  onCopyFromClient={handleCopyFromClient}
                  isCopying={isFetching}
                />
              )}
              {step === STEP.PRICING && (
                <PricingStep pricing={pricing} setPricing={setPricing} />
              )}
              {step === STEP.DOCUMENTS && (
                <DocumentsStep
                  templates={templates}
                  isUploading={isUploading}
                  archivingId={archivingId}
                  onUpload={async (template) => {
                    const created = await uploadTemplate(
                      savedClientId,
                      template
                    );
                    if (created)
                      setTemplates((previous) => [created, ...previous]);
                    return created != null;
                  }}
                  onArchive={async (templateId) => {
                    const isArchived = await archiveTemplate(
                      savedClientId,
                      templateId
                    );
                    if (isArchived) {
                      setTemplates((previous) =>
                        previous.filter(
                          (template) => template.id !== templateId
                        )
                      );
                    }
                  }}
                />
              )}
              {step === STEP.ADMIN && (
                <AdminUserStep
                  adminUsers={adminUsers}
                  isLoading={isCreatingAdmin}
                  // Réglage enregistré : c'est lui que l'API applique à l'envoi.
                  isEmailSendingEnabled={
                    savedClient?.is_email_sending_enabled === 1
                  }
                  onCreate={async (user) => {
                    const users = await createAdminUser(savedClientId, user);
                    if (users) setAdminUsers(users);
                    return users != null;
                  }}
                />
              )}
              {step === STEP.SUMMARY && (
                <Card
                  title="Récapitulatif"
                  subtitle="Chaque étape reste modifiable depuis la liste des clients. Les modifications sont tracées dans l’historique."
                >
                  <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
                    <SummaryRow
                      isDone
                      label="Informations"
                      detail={[
                        information.name,
                        information.siret,
                        information.city,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      onAction={() => setStep(STEP.INFORMATION)}
                    />
                    <SummaryRow
                      isDone={!!savedClient?.logo_url}
                      isWarning={!savedClient?.logo_url}
                      label="Logo"
                      detail={
                        savedClient?.logo_url ? 'Logo enregistré' : 'Aucun logo'
                      }
                      onAction={() => setStep(STEP.INFORMATION)}
                    />
                    <SummaryRow
                      isDone
                      label="Options"
                      detail={`${countEnabled(featuresV2)} options activées`}
                      onAction={() => setStep(STEP.OPTIONS)}
                    />
                    <SummaryRow
                      isDone
                      label="Tarification"
                      detail={`${pricing.price_per_vehicle} € / véhicule · ${pricing.price_per_sms} € / SMS · ${pricing.price_per_email} € / email · ${pricing.price_per_signature ?? '—'} € / signature`}
                      onAction={() => setStep(STEP.PRICING)}
                    />
                    <SummaryRow
                      isDone={!!vroomConfiguration}
                      isWarning={!vroomConfiguration}
                      label="Optimisation VROOM"
                      detail={
                        vroomConfiguration
                          ? `Configuration « ${vroomConfiguration.configuration_name} » (exploration ${vroomConfiguration.exploration_level}, ${vroomConfiguration.timeout} ms) · personnalisation à venir`
                          : 'Aucune configuration : l’optimisation du puits est impossible'
                      }
                      actionText="Créer la configuration par défaut"
                      onAction={
                        vroomConfiguration
                          ? null
                          : async () => {
                              const created =
                                await createVroomConfiguration(savedClientId);
                              if (created)
                                setSavedClient((previous) => ({
                                  ...previous,
                                  vroom_configuration: created,
                                }));
                            }
                      }
                      isActionLoading={isCreatingVroom}
                    />
                    <SummaryRow
                      isDone={templates.length > 0}
                      label="Documents (WIP)"
                      detail={`${templates.length} template${templates.length > 1 ? 's' : ''} · pas encore utilisés pour générer les documents`}
                      onAction={() => setStep(STEP.DOCUMENTS)}
                    />
                    <SummaryRow
                      isDone={adminUsers.length > 0}
                      isWarning={adminUsers.length === 0}
                      label="Administrateur"
                      detail={
                        adminUsers.length > 0
                          ? adminUsers
                              .map(
                                (user) => `${user.first_name} ${user.last_name}`
                              )
                              .join(', ')
                          : 'Aucun administrateur : le client ne peut pas se connecter'
                      }
                      onAction={() => setStep(STEP.ADMIN)}
                    />
                  </div>
                </Card>
              )}
            </>
          )}
        </div>

        {hasUnsavedChanges && (
          <div className="tw-mt-4">
            <Banner tone="warn">
              {unsavedChanges.length} modification
              {unsavedChanges.length > 1 ? 's' : ''} non enregistrée
              {unsavedChanges.length > 1 ? 's' : ''}. Elles seront relues (avant
              / après) à l’enregistrement.
            </Banner>
          </div>
        )}

        <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2">
          {step > STEP.INFORMATION && (
            <MainButton
              variant="secondary"
              text="Précédent"
              onClick={() => setStep((previous) => previous - 1)}
            />
          )}
          <div className="tw-flex-1" />
          {isClientSaved && step !== STEP.PRICING && (
            <MainButton
              variant="secondary"
              text="Enregistrer"
              onClick={() => save()}
              disabled={
                !hasUnsavedChanges || !isInformationValid || !isPricingStepValid
              }
              isLoading={isSaving && !isReviewOpen}
            />
          )}
          {step < STEP.SUMMARY ? (
            <MainButton
              text={nextLabel}
              onClick={goToNextStep}
              disabled={isNextDisabled || !isHydrated}
              isLoading={
                (isSaving && !isReviewOpen) ||
                (step === STEP.PRICING && isLogoUploading)
              }
            />
          ) : (
            <MainButton text="Terminer" onClick={exit} />
          )}
        </div>
      </div>

      <ClientChangesReviewModal
        isOpen={isReviewOpen}
        setIsOpen={setIsReviewOpen}
        clientName={savedClient?.name || information.name}
        diff={unsavedChanges}
        onConfirm={confirmSave}
        isSaving={isSaving}
      />

      {isClientSaved && (
        <ClientHistoryModal
          isOpen={isHistoryOpen}
          setIsOpen={setIsHistoryOpen}
          clientId={savedClientId}
        />
      )}

      <GenericModal
        isOpen={isLeaveConfirmOpen}
        setIsOpen={setIsLeaveConfirmOpen}
        title="Modifications non enregistrées"
        size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
        secondaryActionText="Rester"
        secondaryAction={() => setIsLeaveConfirmOpen(false)}
        mainActionText="Quitter sans enregistrer"
        mainAction={onExit}
      >
        <p className="tw-m-0 tw-text-sm">
          Les modifications du client qui ne sont pas enregistrées (
          {unsavedChanges.length}) seront perdues.
        </p>
      </GenericModal>
    </GenericPageContainer>
  );
}
